/**
 * Phantom-Limb Awareness — informational page only. No data collection,
 * no app-based therapy, no medical claims. Acknowledges the phenomenon,
 * outlines evidence-based management approaches, and links out to the
 * clinical resources users actually need.
 *
 * Behavioral therapy modules (mirror therapy, GMI sequences, sensory
 * desensitization) are gated behind clinical-advisor sign-off — see
 * /app/memory/MASTER_BACKLOG.md deferred items.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Mountain, ExternalLink } from 'lucide-react';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="font-display text-2xl tracking-wide mb-3 text-enf-text-primary">{title}</h2>
    <div className="text-enf-text-secondary text-sm leading-relaxed space-y-3 max-w-3xl">{children}</div>
  </section>
);

const PhantomLimb = () => {
  return (
    <div className="min-h-screen bg-enf-surface-base text-enf-text-primary px-6 py-10" data-testid="phantom-limb-page">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/library?lens=adaptive"
          className="inline-flex items-center gap-2 text-xs text-enf-text-muted hover:text-adaptive-accent mb-6 transition-colors"
        >
          <ArrowLeft className="w-3 h-3" /> Library (with Adaptive lens)
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-adaptive-accent" />
          <span className="text-[10px] uppercase tracking-widest text-adaptive-accent font-bold">Learn · Adaptive</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-3">Phantom-limb awareness</h1>
        <p className="text-enf-text-secondary text-base max-w-3xl mb-12">
          A short, plain-language primer on phantom-limb sensations and pain — what they are, what helps,
          and when to escalate to a clinician. Enferno doesn't deliver therapy here. This page exists so
          you have the right questions to bring to the people who do.
        </p>

        <Section title="Sensations vs pain — they're different">
          <p>
            "Phantom limb" is a catch-all term, but it covers two distinct experiences:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Phantom-limb sensations</strong> — non-painful awareness of the missing limb. Tingling,
              warmth, position, itching, or the feeling that the limb is still there. Extremely common —
              most amputees report some form of these in the first months and many continue to feel them
              for years.
            </li>
            <li>
              <strong>Phantom-limb pain</strong> — painful sensations referred to the missing limb. Burning,
              electric, stabbing, cramping, crushing. Less universal than sensations but still common, and
              can be significantly disruptive when severe.
            </li>
          </ul>
          <p>
            Both are real neurological events — not imagined, not psychological. The brain still holds
            a body map that includes the limb, and that map keeps generating signals.
          </p>
        </Section>

        <Section title="Common patterns">
          <ul className="list-disc pl-6 space-y-2">
            <li>Sensations or pain that intensify with stress, fatigue, weather changes, or socket-fit issues.</li>
            <li>Specific positions or postures of the phantom that feel "stuck" — a clenched fist, a curled toe.</li>
            <li>Triggered episodes around the residual limb being touched, bandaged, or having the prosthesis put on / off.</li>
            <li>Patterns that shift over time — what you feel at 3 months post-amputation may look nothing like what you feel at 3 years.</li>
          </ul>
        </Section>

        <Section title="Evidence-based management approaches">
          <p>
            What follows is informational. Talk to a qualified provider before adopting any of these as
            a personal routine — especially mirror therapy and graded motor imagery, which work best when
            taught and supervised initially.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Mirror therapy</strong> — using a mirror to visually trick the brain into seeing the
              missing limb. Best evidence for upper-limb amputees. Often used with PT supervision in the
              first months.
            </li>
            <li>
              <strong>Graded Motor Imagery (GMI)</strong> — three-stage sequence (laterality recognition →
              imagined movement → mirror therapy). Evidence-based for complex regional pain and phantom
              pain. Requires a trained PT to set up.
            </li>
            <li>
              <strong>Sensory stimulation / desensitization</strong> — controlled touch, vibration, or
              texture exposure on the residual limb. Often the first technique introduced post-surgery.
            </li>
            <li>
              <strong>Mindfulness and acceptance practices</strong> — not a cure, but well-established
              for reducing the disability impact of chronic pain. Apps and therapists both work.
            </li>
            <li>
              <strong>Pharmacological options</strong> — gabapentinoids, certain antidepressants, topical
              agents. A clinician's call, not yours alone.
            </li>
            <li>
              <strong>TENS / electrical stimulation</strong> — over-the-counter units are widely used.
              Evidence is mixed but harm potential is low. Some users find consistent relief.
            </li>
          </ul>
        </Section>

        <Section title="When to escalate">
          <p>Talk to your prosthetist, PT, or physician promptly when:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pain is sustained at 7+ on a 10 scale for more than a few days.</li>
            <li>You're losing sleep on consecutive nights.</li>
            <li>You're noticing increased socket pain or skin breakdown alongside the phantom symptoms.</li>
            <li>Your mood is shifting — depression and chronic phantom pain feed each other, and that loop is treatable.</li>
            <li>Anything new — different sensation, different location, sudden onset — that doesn't fit your usual pattern.</li>
          </ul>
          <p>
            For crisis-level mental health distress, call or text <strong>988</strong> (US Suicide and Crisis Lifeline).
            For non-crisis support, the resources listed below have peer-support networks specifically
            for amputees.
          </p>
        </Section>

        <Section title="Resources & sources">
          <ul className="space-y-2">
            {[
              { name: 'Amputee Coalition — phantom limb pain & sensations', url: 'https://www.amputee-coalition.org/resources/managing-phantom-pain/' },
              { name: 'Limbs for Life Foundation', url: 'https://www.limbsforlife.org/' },
              { name: 'National Limb Loss Resource Center (NLLIC)', url: 'https://www.amputee-coalition.org/resources/national-limb-loss-resource-center/' },
              { name: 'Cochrane Review — Mirror therapy for phantom limb pain', url: 'https://www.cochrane.org/CD010853/SYMPT_mirror-therapy-treating-phantom-limb-pain' },
              { name: 'Noigroup — Graded Motor Imagery (GMI) overview', url: 'https://www.gradedmotorimagery.com/' },
              { name: '988 Suicide and Crisis Lifeline', url: 'https://988lifeline.org/' },
            ].map((r) => (
              <li key={r.url}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-adaptive-accent hover:text-adaptive-accent-hover transition-colors"
                >
                  {r.name} <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            ))}
          </ul>
        </Section>

        <div className="border-t border-enf-surface-border pt-6 mt-12 text-xs text-enf-text-muted leading-relaxed max-w-3xl">
          Enferno surfaces information. Enferno does not diagnose, treat, or substitute for clinical care.
          Decisions about therapy, medication, and device use belong to you and your providers.
        </div>

        <div className="mt-8 flex gap-3">
          <Link
            to="/library?lens=adaptive"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-adaptive-accent hover:bg-adaptive-accent-hover text-enf-text-primary font-semibold transition-colors"
          >
            <Mountain className="w-4 h-4" /> Back to Library
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PhantomLimb;
